import { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import { useTranslation } from "react-i18next";
import { IoArrowBackSharp } from "react-icons/io5";
import { MdOutlineAddCircleOutline } from "react-icons/md";
import { TbTrash, TbTrashOff } from "react-icons/tb";
import { withRouter } from "react-router-dom/cjs/react-router-dom.min";
import Select from "react-select";
import { ToastContainer } from "react-toastify";
import { Button, Col, Form, FormGroup, Input, Label, Row } from "reactstrap";
import swal from "sweetalert";
import {
  CheckPermission,
  customOption,
  customSingleValue,
  LoadingSpinner,
} from "../../../constants/const";
import { encryptStorage } from "../../../constants/EncryptStorage";
import { ApiFinance } from "../../config/finance/ApiFinance";
import { GetLanguageString } from "../../helper/Components";
import { GetCustomStyles, GetSelectedLanguage } from "../../helper/Method";
import { ApiFinance as ApiFinanceLocal } from "../ApiFinance";
import { viewReceiptPdf } from "../../shared/receiptViewer";
import { getMinMaxDates, validateTransactionDate } from "../fiscalYearHelper";

// ---------------- Reusable Components ----------------
const CustomSelect = ({
  label,
  name,
  options,
  value,
  onChange,
  loading,
  error,
  placeholder,
  isDisabled,
}) => (
  <FormGroup>
    <Label className="col-form-label">
      {<GetLanguageString props={label} />}
    </Label>
    <Select
      components={{ Option: customOption, SingleValue: customSingleValue }}
      options={options}
      name={name}
      value={value}
      className="basic-select"
      classNamePrefix="select"
      isLoading={loading}
      isDisabled={isDisabled || options.length === 0}
      onChange={onChange}
      placeholder={placeholder}
      styles={{
        control: (provided, state) => ({
          ...provided,
          borderColor: error ? "#dc3545" : state.isFocused ? "#1ab394" : "#e5e6e7",
          "&:hover": {
            borderColor: error ? "#dc3545" : state.isFocused ? "#1ab394" : "#d2d2d2",
          },
        }),
      }}
    />
    {error && <h4 className="text-error">{error}</h4>}
  </FormGroup>
);

const CustomInput = ({ label, name, value, onChange, error, ...rest }) => (
  <FormGroup>
    <Label className="col-form-label">
      {<GetLanguageString props={label} />}
    </Label>
    <Input
      name={name}
      type="text"
      value={value}
      onChange={onChange}
      className="no-drag"
      invalid={!!error}
      {...rest}
    />
    {error && <h4 className="text-error no-drag">{error}</h4>}
  </FormGroup>
);

// ---------------- Main Component ----------------
// ---------------- Helper Functions ----------------
const isSpecialCategory = (categoryCode) => {
  return (
    categoryCode === "loan_individual" ||
    categoryCode === "infaq_rafeeqat"
  );
};

const categorySupportsQtyPrice = (categoryCode) => {
  return (
    categoryCode === "asset" ||
    categoryCode === "maktaba"
  );
};

// ---------------- Main Component ----------------
const OtherIncome = ({ history }) => {
  const { t } = useTranslation();
  const loggedInUnitId = parseInt(encryptStorage.getItem("loggedInUnitId"));
  const unitTypeName = encryptStorage.getItem("unitTypeName");

  // ---------- State ----------
  const [loading, setLoading] = useState({
    source: false,
    submit: false,
  });

  const [financeCategories, setFinanceCategories] = useState([]);

  const [formData, setFormData] = useState({
    date: new Date(),
    memo: "",
    receiptNumber: "",
    entries: [
      {
        selectedCategory: null,
        financeAccount: null,
        dimension2: null,
        accounts: [],
        dimensions: [],
        amount: "",
        quantity: "",
        price: "",
        lineMemo: "",
        serialNumber: "",
      },
    ],
  });

  const [errors, setErrors] = useState({});
  const [receiptType, setReceiptType] = useState("physical");

  const viewReceipt = (transactionNo) =>
    viewReceiptPdf({
      transactionNo,
      type: "otherIncome",
      t,
      onSuccess: () => setTimeout(() => history.goBack(), 4000),
    }).catch(() => false);

  // ---------- Helpers ----------
  const fetchAccountsAndDimensions = (index, categoryId) => {
    if (!categoryId) return;

    ApiFinance()
      .GetFinanceSubCategoryAccountsById(categoryId)
      .then((res) => {
        setFormData((prev) => {
          const updated = prev.entries.map((entry, idx) => {
            if (idx === index) {
              return {
                ...entry,
                accounts: res.data || [],
                financeAccount: res.data?.[0] || null,
              };
            }
            return entry;
          });
          return { ...prev, entries: updated };
        });
      })
      .catch(console.error);

    ApiFinance()
      .GetFinanceSubCategoryDimensionsById(categoryId)
      .then((res) => {
        setFormData((prev) => {
          const updated = prev.entries.map((entry, idx) => {
            if (idx === index) {
              return {
                ...entry,
                dimensions: res.data || [],
                dimension2: res.data?.[0] || null,
              };
            }
            return entry;
          });
          return { ...prev, entries: updated };
        });
      })
      .catch(console.error);
  };

  // ---------- Handlers ----------
  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const getTotalAmount = (entries) => {
    const total = entries.reduce(
      (sum, entry) => sum + (parseFloat(entry.amount) || 0),
      0,
    );
    return Number.isInteger(total) ? total.toString() : total.toFixed(2);
  };

  const handleEntryChange = (index, field, value) => {
    setFormData((prev) => {
      const updatedEntries = prev.entries.map((entry, idx) => {
        if (idx === index) {
          const updated = { ...entry, [field]: value };

          if (field === "selectedCategory") {
            updated.financeAccount = null;
            updated.dimension2 = null;
            updated.accounts = [];
            updated.dimensions = [];
            updated.serialNumber = "";
            const isQtyPrice = categorySupportsQtyPrice(value?.label1);
            if (!isQtyPrice) {
              updated.quantity = "";
              updated.price = "";
            }
            if (value?.value) {
              fetchAccountsAndDimensions(index, value.value);
            }
          }

          // Calculate amount if quantity or price changes
          const categoryCode = updated.selectedCategory?.label1;
          if (categorySupportsQtyPrice(categoryCode)) {
            if (field === "quantity" || field === "price" || field === "selectedCategory") {
              const q = parseFloat(updated.quantity);
              const p = parseFloat(updated.price);
              if (!isNaN(q) && !isNaN(p)) {
                updated.amount = (q * p).toString();
              } else {
                updated.amount = "";
              }
            }
          }

          return updated;
        }
        return entry;
      });
      return { ...prev, entries: updatedEntries };
    });
    setErrors((prev) => {
      const updatedEntries = Array.isArray(prev.entries) ? [...prev.entries] : [];
      if (updatedEntries[index]) {
        if (field === "selectedCategory") {
          updatedEntries[index] = {
            ...updatedEntries[index],
            selectedCategory: "",
            financeAccount: "",
            dimension2: "",
            amount: "",
            quantity: "",
            price: "",
            serialNumber: "",
          };
        } else {
          updatedEntries[index] = {
            ...updatedEntries[index],
            [field]: "",
          };
        }
      }
      return { ...prev, entries: updatedEntries };
    });
  };

  const addEntry = () => {
    const defaultCategory = financeCategories[0] || null;
    const nextIndex = formData.entries.length;
    setFormData((prev) => ({
      ...prev,
      entries: [
        ...prev.entries,
        {
          selectedCategory: defaultCategory,
          financeAccount: null,
          dimension2: null,
          accounts: [],
          dimensions: [],
          amount: "",
          quantity: "",
          price: "",
          lineMemo: "",
          serialNumber: "",
        },
      ],
    }));
    setErrors((prev) => {
      if (Array.isArray(prev.entries)) {
        return {
          ...prev,
          entries: [...prev.entries, {}]
        };
      }
      return prev;
    });
    if (defaultCategory?.value) {
      fetchAccountsAndDimensions(nextIndex, defaultCategory.value);
    }
  };

  const removeEntry = (index) => {
    setFormData((prev) => ({
      ...prev,
      entries: prev.entries.filter((_, idx) => idx !== index),
    }));
    setErrors((prev) => {
      if (Array.isArray(prev.entries)) {
        return {
          ...prev,
          entries: prev.entries.filter((_, idx) => idx !== index)
        };
      }
      return prev;
    });
  };

  const handleReceiptNumber = (e) => {
    let { value } = e.target;
    value = value.replace(/\D/g, "");
    setFormData((prev) => ({
      ...prev,
      receiptNumber: value,
    }));
    setErrors((prev) => ({ ...prev, receiptNumber: "" }));
  };

  const handleReceiptTypeChange = (e) => {
    const value = e.target.value;
    setReceiptType(value);
    if (value === "digital") {
      setFormData((prev) => ({
        ...prev,
        receiptNumber: "",
      }));
      setErrors((prev) => ({ ...prev, receiptNumber: "" }));
    }
  };

  const formatSelectListModel = (item) => {
    if (!item) return null;
    return {
      value: parseInt(item.value, 10) || 0,
      label: item.label ? String(item.label) : "",
      label1: item.label1 ? String(item.label1) : "",
    };
  };

  const handleSubmit = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const isValid = validate();
    if (!isValid) {
      return;
    }

    const totalAmount = getTotalAmount(formData.entries);

    const entriesPayload = formData.entries.map((entry) => {
      const categoryCode = entry.selectedCategory?.label1;
      const isQtyPrice = categorySupportsQtyPrice(categoryCode);
      const fallbackMemo = entry.lineMemo || formData.memo;
      return {
        category: formatSelectListModel(entry.selectedCategory),
        financeAccount: formatSelectListModel(entry.financeAccount),
        dimension2: formatSelectListModel(entry.dimension2),
        amount: parseFloat(entry.amount) || 0,
        lineMemo: fallbackMemo,
        quantity: isQtyPrice && entry.quantity ? parseInt(entry.quantity, 10) : null,
        price: isQtyPrice && entry.price ? parseFloat(entry.price) : null,
      };
    });

    const loanRow = formData.entries.find(e => e.selectedCategory?.label1 === "loan_individual");
    const serialNumber = loanRow ? loanRow.serialNumber : "";

    const dateObj = new Date(formData.date);
    const isoDate = !isNaN(dateObj.getTime()) ? dateObj.toISOString() : formData.date;

    const payload = {
      category: entriesPayload[0]?.category ?? null,
      financeAccount: entriesPayload[0]?.financeAccount ?? null,
      dimension2: entriesPayload[0]?.dimension2 ?? null,
      date: isoDate,
      amount: parseFloat(totalAmount) || 0,
      serialNumber: serialNumber,
      memo: formData.memo,
      unitId: loggedInUnitId,
      receiptNumber: formData.receiptNumber,
      generateDigitalReceipt: receiptType === "digital",
      entries: entriesPayload,
      quantity: entriesPayload[0]?.quantity ?? null,
      price: entriesPayload[0]?.price ?? null,
    };

    setLoading((prev) => ({ ...prev, submit: true }));
    ApiFinanceLocal()
      .AddOtherIncome(payload)
      .then((res) => {
        const data = res.data;

        if (data?.statusCode === 200) {
          const transactionNo = data?.transactionNo;
          const shouldShowReceipt =
            transactionNo && Number(transactionNo) !== 0;

          swal({
            title: "Success",
            text:
              (typeof data?.message === "string" && data.message ? t(data.message) : "") ||
              t("funds_other_income_added_successfully"),
            icon: "success",
            buttons: shouldShowReceipt
              ? {
                OK: true,
                viewReceipt: {
                  text: t("common_view_receipt") || "View Receipt",
                  value: "viewReceipt",
                },
              }
              : "OK",
            className: GetSelectedLanguage() === "en" ? "" : "ur",
          }).then((value) => {
            if (value === "viewReceipt") {
              viewReceipt(transactionNo);
              return;
            }
            history.goBack();
          });
          setLoading((prev) => ({ ...prev, submit: false }));
          return;
        }

        swal({
          title: "Error",
          text:
            typeof data?.message === "string"
              ? data.message
              : "Failed to add Other Income",
          icon: "error",
          buttons: "OK",
        });

        setLoading((prev) => ({ ...prev, submit: false }));
      })
      .catch((err) => {
        let errorMessage = "Unable to connect to server. Please try again.";
        if (typeof err?.response?.data === "string" && err.response.data.trim()) {
          errorMessage = err.response.data;
        } else if (err?.response?.data?.message && typeof err.response.data.message === "string") {
          errorMessage = err.response.data.message;
        } else if (err?.response?.data?.title && typeof err.response.data.title === "string") {
          errorMessage = err.response.data.title;
        } else if (err?.message && typeof err.message === "string") {
          errorMessage = err.message;
        }

        swal({
          title: "Error",
          text: String(errorMessage),
          icon: "error",
          buttons: "OK",
        });
        setLoading((prev) => ({ ...prev, submit: false }));
      });
  };

  // ---------- Effects ----------
  // Fetch finance categories on mount
  useEffect(() => {
    setLoading((prev) => ({ ...prev, source: true }));
    ApiFinance()
      .GetFinanceSubCategoriesListByType("income")
      .then((res) => {
        const filtered = (res.data || []).filter((cat) => {
          // Exclude Markaz, Tanzeem, Help always
          if (
            cat.label1.includes("markaz") ||
            cat.label1.includes("tanzeem") ||
            cat.label1.includes("help") ||
            cat.label1.includes("reimbursment")
          ) {
            return false;
          }

          // If unitTypeName is not Halqa, exclude labels with Course or Maktaba
          if (unitTypeName !== "Halqa" && cat.label1.includes("course")) {
            return false;
          }
          if (
            unitTypeName !== "Halqa" &&
            (cat.label1?.toLowerCase().includes("maktaba") ||
              (cat.label || cat.name || cat.category || "").toLowerCase().includes("maktaba"))
          ) {
            return false;
          }
          if (cat.label1 === "infaq") {
            return false;
          }

          return true; // keep all other categories
        });

        const formatted = filtered.map((c) => ({
          value: c.value || c.id,
          label: c.label || c.category,
          label1: c.label1 || c.code,
        }));
        setFinanceCategories(formatted);

        if (formatted.length > 0) {
          const defaultCategory = formatted[0];
          setFormData((prev) => ({
            ...prev,
            entries: [
              {
                selectedCategory: defaultCategory,
                financeAccount: null,
                dimension2: null,
                accounts: [],
                dimensions: [],
                amount: "",
                quantity: "",
                price: "",
                lineMemo: "",
                serialNumber: "",
              },
            ],
          }));
          fetchAccountsAndDimensions(0, defaultCategory.value);
        }
      })
      .catch((err) => {
        console.error(err);
        swal({
          title: "Error",
          text: err.response?.data || "Failed to load categories",
          icon: "error",
          buttons: "OK",
        });
      })
      .finally(() => setLoading((prev) => ({ ...prev, source: false })));
  }, [unitTypeName]);

  // ---------- Validation ----------
  const validate = () => {
    const temp = {};
    const entriesErrors = [];
    let hasInvalidEntry = false;

    for (let i = 0; i < formData.entries.length; i++) {
      const entry = formData.entries[i];
      const entryErrors = {};

      if (!entry.selectedCategory) {
        entryErrors.selectedCategory = t("common_field_required_error");
        hasInvalidEntry = true;
      } else {
        const categoryCode = entry.selectedCategory.label1;
        const isSpecial = isSpecialCategory(categoryCode);
        const isQtyPrice = categorySupportsQtyPrice(categoryCode);

        // Validate Amount: empty vs <= 0
        if (!isQtyPrice) {
          if (!entry.amount || entry.amount.trim() === "") {
            entryErrors.amount = t("common_field_required_error");
            hasInvalidEntry = true;
          } else {
            const parsedAmount = parseFloat(entry.amount);
            if (isNaN(parsedAmount) || parsedAmount <= 0) {
              entryErrors.amount = t("amount_must_be_greater_than_zero");
              hasInvalidEntry = true;
            }
          }
        }

        if (!isSpecial) {
          if (!entry.financeAccount) {
            entryErrors.financeAccount = t("common_field_required_error");
            hasInvalidEntry = true;
          }
        }

        const showSubHead = categoryCode !== "tarbiati_course" && categoryCode !== "misc" && !isSpecial;
        if (showSubHead) {
          if (!entry.dimension2) {
            entryErrors.dimension2 = t("common_field_required_error");
            hasInvalidEntry = true;
          }
        }

        if (isQtyPrice) {
          // Validate Quantity
          if (!entry.quantity || entry.quantity.trim() === "") {
            entryErrors.quantity = t("common_field_required_error");
            hasInvalidEntry = true;
          } else {
            const parsedQty = parseFloat(entry.quantity);
            if (isNaN(parsedQty) || parsedQty <= 0) {
              entryErrors.quantity = t("quantity_must_be_greater_than_zero");
              hasInvalidEntry = true;
            }
          }

          // Validate Price
          if (!entry.price || entry.price.trim() === "") {
            entryErrors.price = t("common_field_required_error");
            hasInvalidEntry = true;
          } else {
            const parsedPrice = parseFloat(entry.price);
            if (isNaN(parsedPrice) || parsedPrice <= 0) {
              entryErrors.price = t("price_must_be_greater_than_zero");
              hasInvalidEntry = true;
            }
          }
        }

        if (categoryCode === "loan_individual") {
          if (!entry.serialNumber || !entry.serialNumber.trim()) {
            entryErrors.serialNumber = t("common_field_required_error");
            hasInvalidEntry = true;
          }
        }
      }

      entriesErrors.push(entryErrors);
    }

    temp.entries = hasInvalidEntry ? entriesErrors : [];

    if (!formData.memo || !formData.memo.trim()) {
      temp.memo = t("common_field_required_error");
      hasInvalidEntry = true;
    }

    temp.receiptNumber =
      receiptType === "physical" ? (
        formData.receiptNumber ? (
          ""
        ) : (
          t("common_field_required_error")
        )
      ) : (
        ""
      );

    const dateError = validateTransactionDate(formData.date);
    if (dateError) {
      temp.date = t(dateError);
    }

    setErrors({
      ...temp,
    });

    const isReceiptValid = receiptType !== "physical" || !!formData.receiptNumber;

    return !hasInvalidEntry && isReceiptValid && !temp.date;
  };

  // ---------- Render ----------
  return (
    <>
      <ToastContainer
        autoClose={3000}
        bodyClassName={GetSelectedLanguage()}
        closeOnClick
        pauseOnHover={false}
        draggable
      />
      <div className="wrapper-custom wrapper wrapper-content animated fadeInRight">
        <div className="ibox">
          <div className="ibox-title d-flex justify-content-between align-items-center">
            <h5>{<GetLanguageString props="funds_other_income" />}</h5>
            <Button
              className={GetCustomStyles().btn_style}
              color="white"
              size="xs"
              onClick={() => history.goBack()}
            >
              <IoArrowBackSharp
                color="#18A689"
                size={15}
                style={{ marginRight: 5, marginBottom: 1 }}
              />
              {<GetLanguageString props="courses_participants_back" />}
            </Button>
          </div>

          <div className="ibox-content">
            {loading.source || loading.submit ? (
              LoadingSpinner()
            ) : (
              <Form onSubmit={handleSubmit} noValidate>
                <Row>
                  <Col md="3">
                    <FormGroup>
                      <Label className="col-form-label">
                        {<GetLanguageString props="funds_date" />}
                      </Label>
                      <DatePicker
                        showYearDropdown
                        dropdownMode="select"
                        placeholderText="dd/mm/yyyy"
                        className="form-control no-drag"
                        selected={formData.date}
                        minDate={getMinMaxDates(formData.date).minDate}
                        maxDate={getMinMaxDates(formData.date).maxDate}
                        onChange={(date) =>
                          handleChange("date", date ?? new Date())
                        }
                        dateFormat="dd/MM/yyyy"
                      />
                      {errors.date && (
                        <h4 className="text-error">{errors.date}</h4>
                      )}
                    </FormGroup>
                  </Col>
                  <Col md="3">
                    <FormGroup>
                      <div
                        className="col-form-label d-flex align-items-center"
                        style={{ gap: 16 }}
                      >
                        <Label check className="mb-0">
                          <Input
                            type="radio"
                            name="otherIncomeReceiptType"
                            value="physical"
                            checked={receiptType === "physical"}
                            onChange={handleReceiptTypeChange}
                          />
                          {<GetLanguageString props="funds_physical_receipt" />}
                        </Label>
                        <Label check className="mb-0">
                          <Input
                            type="radio"
                            name="otherIncomeReceiptType"
                            value="digital"
                            checked={receiptType === "digital"}
                            onChange={handleReceiptTypeChange}
                          />
                          {<GetLanguageString props="funds_digital_receipt" />}
                        </Label>
                      </div>
                      <Input
                        autoFocus
                        type="text"
                        className="no-drag"
                        value={formData.receiptNumber}
                        onChange={handleReceiptNumber}
                        disabled={receiptType === "digital"}
                      />
                      {errors.receiptNumber && (
                        <h4 className="text-error">{errors.receiptNumber}</h4>
                      )}
                    </FormGroup>
                  </Col>
                  <Col md="3">
                    <CustomInput
                      name="memo"
                      label="transactions_memo"
                      value={formData.memo}
                      onChange={(e) => handleChange("memo", e.target.value)}
                      error={errors.memo}
                    />
                  </Col>
                  <Col md="3">
                    <CustomInput
                      name="totalAmount"
                      label="finance_other_income_total_amount"
                      value={getTotalAmount(formData.entries)}
                      readOnly
                      disabled
                      error={errors.amount}
                    />
                  </Col>
                </Row>
                <div className="ibox">
                  <div className="ibox-title d-flex justify-content-between align-items-center">
                    <h5>
                      {
                        <GetLanguageString props="finance_other_income_entry_details" />
                      }
                    </h5>
                    <MdOutlineAddCircleOutline
                      size={25}
                      color="#3ba147"
                      onClick={addEntry}
                      style={{ marginRight: 6, marginBottom: 1, cursor: "pointer" }}
                    />
                  </div>
                  <div className="ibox-content">
                    {formData.entries.map((entry, index) => {
                      const categoryCode = entry.selectedCategory?.label1;
                      const isLoanIndividual = categoryCode === "loan_individual";
                      const isAssetOrMaktaba = categoryCode === "asset" || categoryCode === "maktaba";
                      const isSpecial = isSpecialCategory(categoryCode);
                      const isQtyPrice = categorySupportsQtyPrice(categoryCode);
                      const disableIncomeHead = categoryCode === "maktaba" || categoryCode === "misc";
                      const showIncomeSubHead = categoryCode !== "tarbiati_course" && categoryCode !== "misc" && !isSpecial;
                      const isMaktaba = categoryCode === "maktaba";

                      return (
                        <Row key={index} className="mb-3 align-items-start">
                          <Col md>
                            <CustomSelect
                              name={`category-${index}`}
                              label="finance_tab_category"
                              options={financeCategories}
                              value={entry.selectedCategory}
                              onChange={(val) =>
                                handleEntryChange(index, "selectedCategory", val)
                              }
                              error={errors.entries?.[index]?.selectedCategory}
                              placeholder={t("member_select")}
                            />
                          </Col>
                          {isLoanIndividual && (
                            <Col md>
                              <CustomInput
                                name={`serialNumber-${index}`}
                                label="notifications_rafiq_serial_number"
                                value={entry.serialNumber || ""}
                                onChange={(e) =>
                                  handleEntryChange(index, "serialNumber", e.target.value)
                                }
                                error={errors.entries?.[index]?.serialNumber}
                              />
                            </Col>
                          )}
                          {!isSpecial && (
                            <Col md>
                              <CustomSelect
                                name={`financeAccount-${index}`}
                                label="funds_income_head"
                                options={entry.accounts || []}
                                value={entry.financeAccount}
                                onChange={(val) =>
                                  handleEntryChange(index, "financeAccount", val)
                                }
                                error={errors.entries?.[index]?.financeAccount}
                                isDisabled={disableIncomeHead || !entry.selectedCategory}
                                placeholder={
                                  !entry.selectedCategory
                                    ? t("finance_other_income_income_heads_select")
                                    : (entry.accounts || []).length > 0
                                      ? t("finance_other_income_income_heads_select")
                                      : t("finance_other_income_income_heads_empty")
                                }
                              />
                            </Col>
                          )}
                          {showIncomeSubHead && (
                            <Col md>
                              <CustomSelect
                                name={`dimension2-${index}`}
                                label="funds_income_sub_head"
                                options={entry.dimensions || []}
                                value={entry.dimension2}
                                onChange={(val) =>
                                  handleEntryChange(index, "dimension2", val)
                                }
                                error={errors.entries?.[index]?.dimension2}
                                isDisabled={!entry.selectedCategory}
                                placeholder={
                                  !entry.selectedCategory
                                    ? t("finance_other_income_income_sub_heads_select")
                                    : (entry.dimensions || []).length > 0
                                      ? t("finance_other_income_income_sub_heads_select")
                                      : t("finance_other_income_income_sub_heads_empty")
                                }
                              />
                            </Col>
                          )}
                          <Col md={isAssetOrMaktaba ? "1" : null}>
                            <CustomInput
                              name={`entryAmount-${index}`}
                              label="funds_add_fund_amount"
                              value={entry.amount}
                              onChange={(e) =>
                                handleEntryChange(index, "amount", e.target.value)
                              }
                              error={errors.entries?.[index]?.amount}
                              readOnly={isQtyPrice}
                              disabled={isQtyPrice}
                            />
                          </Col>
                          {isQtyPrice && (
                            <Col md="1">
                              <CustomInput
                                name={`quantity-${index}`}
                                label="finance_other_income_quantity"
                                value={entry.quantity}
                                onChange={(e) =>
                                  handleEntryChange(index, "quantity", e.target.value)
                                }
                                error={errors.entries?.[index]?.quantity}
                              />
                            </Col>
                          )}
                          {isQtyPrice && (
                            <Col md={isMaktaba ? "2" : "1"}>
                              <CustomInput
                                name={`price-${index}`}
                                label="finance_other_income_price"
                                value={entry.price}
                                onChange={(e) =>
                                  handleEntryChange(index, "price", e.target.value)
                                }
                                error={errors.entries?.[index]?.price}
                              />
                            </Col>
                          )}
                          {!isMaktaba && (
                            <Col md>
                              <CustomInput
                                name={`lineMemo-${index}`}
                                label="transactions_memo"
                                value={entry.lineMemo}
                                onChange={(e) =>
                                  handleEntryChange(index, "lineMemo", e.target.value)
                                }
                                error={errors.entries?.[index]?.lineMemo}
                              />
                            </Col>
                          )}
                          <Col
                            md="1"
                            className="text-center"
                          >
                            <Label className="col-form-label d-block">&nbsp;</Label>
                            {formData.entries.length === 1 ? (
                              <TbTrashOff
                                size={25}
                                color="#a72424"
                                style={{ opacity: 0.5 }}
                              />
                            ) : (
                              <TbTrash
                                size={25}
                                color="#bb0e0e"
                                style={{ cursor: "pointer" }}
                                onClick={() => removeEntry(index)}
                              />
                            )}
                          </Col>
                        </Row>
                      );
                    })}
                  </div>
                </div>
                <Row>
                  <Col md="9"></Col>
                  <Col md="3" className="text-end">
                    {CheckPermission("CanAddFunds") ? (
                      <>
                        <Button color="white" onClick={() => history.goBack()}>
                          {<GetLanguageString props="common_cancel" />}
                        </Button>
                        &nbsp;&nbsp;
                        <Button
                          disabled={Object.values(loading).some(
                            (val) => val === true,
                          )}
                          color="primary"
                          onClick={() => handleSubmit(false)}
                        >
                          {<GetLanguageString props="funds_add_income" />}
                        </Button>
                      </>
                    ) : null}
                  </Col>
                </Row>
              </Form>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default withRouter(OtherIncome);
